import type { ReactNode } from 'react'
import { useMyMissions, useMyFleet, useDefense, useForge, usePvpRaids, useChantier } from '@/api/hooks'
import { useCountdown } from '@/hooks/useCountdown'
import { MissionCard } from '@/components/MissionCard'
import { collectInProgress, groupInProgress, type InProgressItem, type InProgressCategory } from '@/lib/inProgress'
import { useViewMode, ViewToggle, useCollapse, CollapseAllButton, CollapsibleGroup } from '@/components/game/collapse'
import { CompassIcon, BuildingIcon, BuildingsIcon, FortIcon, DefenseIcon, AnchorIcon, ShipIcon, ForgeIcon, ForgeBranchIcon, CombatIcon, LanternIcon } from '@/components/game/GameIcons'
import { CancelMissionButton } from '@/components/CancelMissionButton'
import { isMissionCancellable } from '@/lib/missionCancel'

/**
 * Dashboard "En cours" — everything with a build/resolve timer (missions,
 * building upgrades, ship construction) in one feed, grouped by category,
 * collapsible (closed by default) with a grid (blocs) / list toggle.
 */
function categoryIcon(category: InProgressCategory): ReactNode {
  const cls = 'w-5 h-5 text-sand-300'
  if (category === 'building') return <BuildingsIcon className={cls} />
  if (category === 'defense') return <FortIcon className={cls} />
  if (category === 'ship') return <AnchorIcon className={cls} />
  if (category === 'forge') return <ForgeIcon className={cls} />
  if (category === 'chantier') return <LanternIcon className={cls} />
  // PvP raids share the neutral gold of every other category header here — the
  // "En cours" feed is a uniform timer list, not a place to flag combat.
  if (category === 'pvp_raid') return <CombatIcon className={cls} />
  return <CompassIcon className={cls} />
}

function itemIcon(item: InProgressItem): ReactNode {
  if (item.category === 'building') return <BuildingIcon type={item.buildingType ?? 'hq'} className="w-5 h-5 text-sand-300" />
  if (item.category === 'defense') return <DefenseIcon type={item.defenseType ?? 'fort'} className="w-5 h-5 text-sand-300" />
  if (item.category === 'forge') return <ForgeBranchIcon branch={item.forgeBranch ?? 'modules'} className="w-5 h-5 text-sand-300" />
  if (item.category === 'pvp_raid') return <CombatIcon className="w-5 h-5 text-sand-300" />
  if (item.category === 'chantier') return <LanternIcon className="w-5 h-5 text-sand-300" />
  return <ShipIcon type={item.shipType ?? 'brick'} className="w-5 h-5 text-sand-300" />
}

/** Compact ONE-line row (list mode), deliberately thin. For missions the ship
 * count is folded into the title as "Type (N)" and — while the fleet is still
 * recallable — the cancel button sits inline next to the timer. No fleet-
 * composition chip here (that detail lives in the grid/tile MissionCard + the
 * map) so the list stays ultra-compact. */
export function ProgressRow({ item }: { item: InProgressItem }) {
  const countdown = useCountdown(item.resolvesAt)
  const shipCount = (item.mission?.fleet ?? []).reduce((sum, f) => sum + f.count, 0)
  const canCancel = item.mission ? isMissionCancellable(item.mission) : false
  return (
    <li className="rounded-xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 px-3 py-2.5">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-9 h-9 rounded-lg bg-ocean-900/50 border border-sand-500/25 shrink-0">
          {itemIcon(item)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-base text-sand-300 truncate">
            {item.title}{shipCount > 0 ? ` (${shipCount})` : ''}
          </p>
          {item.subtitle && <p className="text-[10px] text-ocean-200 uppercase tracking-wider truncate">{item.subtitle}</p>}
        </div>
        {/* Timer + (si la flotte est encore rappelable) le bouton d'annulation,
            sur la même ligne — la vue liste reste sur UNE seule ligne fine. */}
        <div className="shrink-0 flex items-center gap-2">
          <span className="text-right whitespace-nowrap">
            <span className="block text-[10px] text-ocean-300 uppercase tracking-wider">Reste</span>
            <span className="block font-display text-sand-100 tabular-nums">{countdown}</span>
          </span>
          {canCancel && item.mission && <CancelMissionButton mission={item.mission} />}
        </div>
      </div>
    </li>
  )
}

/** Block (grid mode) for non-mission items — missions reuse the richer MissionCard. */
function ProgressBlock({ item }: { item: InProgressItem }) {
  const countdown = useCountdown(item.resolvesAt)
  return (
    <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 space-y-3">
      <div className="flex items-center gap-2.5">
        <span className="grid place-items-center w-10 h-10 rounded-lg bg-ocean-900/50 border border-sand-500/25 shrink-0">
          {itemIcon(item)}
        </span>
        <div className="min-w-0">
          <h3 className="font-display text-lg text-sand-300 truncate">{item.title}</h3>
          {item.subtitle && <p className="text-[11px] text-ocean-200 uppercase tracking-wider truncate">{item.subtitle}</p>}
        </div>
      </div>
      <div className="bg-ocean-900/55 rounded-lg p-2 text-center border border-sand-500/10">
        <p className="text-[11px] text-ocean-300 uppercase tracking-wider">Temps restant</p>
        <p className="font-display text-lg text-sand-100 tabular-nums">{countdown}</p>
      </div>
    </div>
  )
}

export function InProgressPanel() {
  const missions = useMyMissions()
  const { data: fleet } = useMyFleet()
  const { data: defense } = useDefense()
  const { data: forge } = useForge()
  const { data: pvp } = usePvpRaids()
  const { data: chantier } = useChantier()
  const [view, setView] = useViewMode('corsalia.dashboard.view')
  const collapse = useCollapse()

  const items = collectInProgress(
    missions.data?.missions ?? [],
    fleet?.ships ?? [],
    fleet?.buildings ?? [],
    defense?.structures ?? [],
    forge?.activeResearch ?? null,
    pvp?.sent ?? [],
    chantier?.openTier ?? null,
  )
  if (items.length === 0) return null

  const groups = groupInProgress(items)
  const keys = groups.map((g) => g.category)

  return (
    <section>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="font-display text-xl text-sand-100">En cours ({items.length})</h2>
        <div className="flex items-center gap-2">
          <CollapseAllButton keys={keys} collapse={collapse} />
          <ViewToggle view={view} onChange={setView} />
        </div>
      </div>

      <div className="space-y-4">
        {groups.map((g) => (
          <CollapsibleGroup
            key={g.category}
            title={g.label}
            count={g.items.length}
            icon={categoryIcon(g.category)}
            open={collapse.isOpen(g.category)}
            onToggle={() => collapse.toggle(g.category)}
          >
            {view === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {g.items.map((it) =>
                  it.mission ? <MissionCard key={it.id} mission={it.mission} /> : <ProgressBlock key={it.id} item={it} />,
                )}
              </div>
            ) : (
              <ul className="space-y-2">
                {g.items.map((it) => (
                  <ProgressRow key={it.id} item={it} />
                ))}
              </ul>
            )}
          </CollapsibleGroup>
        ))}
      </div>
    </section>
  )
}
